import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import api from "@/lib/api";

// Same quota surface AiPanel uses (GET /ai/quota, ai-quota / ai-quota-blocked), packaged for the other tabs.
export function useAiQuota() {
  const [quota, setQuota] = useState(null);
  const load = () => api.get("/ai/quota").then((r) => setQuota(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const exhausted = !!quota && quota.mode !== "own" && quota.remaining === 0;
  const notAllowed = !!quota && !quota.allowed;
  return { quota, reload: load, canRun: !!quota && !exhausted && !notAllowed, exhausted, notAllowed };
}

export function AiActionButton({ testid, onClick, busy, label, busyLabel = "Drafting…", q, className = "pp-btn-ghost text-xs py-1.5" }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick} disabled={busy || !q.canRun} className={className}>
      <Sparkles size={13} /> {busy ? busyLabel : q.notAllowed ? "AI not available for your role" : q.exhausted ? "Monthly AI quota used" : label}
    </button>
  );
}

// One line for every counter surface: allowance for the acting workspace, funding org named when it isn't the acting org.
export function quotaLine(quota) {
  if (!quota) return "";
  if (quota.mode === "own") return `Running on your own ${quota.own_key.provider_name} key — no platform limit`;
  const credits = quota.topup_credits ? ` · ${quota.topup_credits} top-up credits ${quota.topup_allowed ? "in reserve" : "held (drawing is off)"}` : "";
  const funded = quota.funded_by ? ` — funded by ${quota.funded_by.name}` : "";
  return `${quota.remaining} of ${quota.quota} AI drafts left this month in this workspace${funded} · resets ${quota.resets_on}${credits}`;
}

// Exhaustion: a partner gets buy/allocate inline; a client whose partner funds the workspace is pointed at the partner, never at a payment form.
export function QuotaExhaustedActions({ quota, testidPrefix }) {
  if (!quota) return null;
  if (quota.actor === "partner") return <> <Link to="/app/settings#topups" data-testid={`${testidPrefix}-ai-topup-link`} className="font-bold underline">Buy or allocate credits for this workspace</Link> from your practice pool.</>;
  if (quota.funded_by) return <span data-testid={`${testidPrefix}-ai-contact-partner`}> This workspace's AI is funded by <span className="font-bold">{quota.funded_by.name}</span> — ask them to add top-up credits. Meanwhile the <Link to="/app/learn" className="font-bold underline">Learn articles</Link> cover the ground.</span>;
  return <> <Link to="/app/settings#topups" data-testid={`${testidPrefix}-ai-topup-link`} className="font-bold underline">Buy a top-up pack</Link> or <Link to="/pricing" className="font-bold underline">see plans</Link>.</>;
}

export function AiQuotaNote({ q, blocked, testidPrefix }) {
  return (
    <>
      {q.quota && <div data-testid={`${testidPrefix}-ai-quota`} className="text-[11px] text-muted-foreground">{quotaLine(q.quota)}</div>}
      {blocked && <div data-testid={`${testidPrefix}-ai-quota-blocked`} role="alert" className="rounded-xl border border-warning-200 bg-warning-50 p-3 text-sm text-warning-900">{blocked}<QuotaExhaustedActions quota={q.quota} testidPrefix={testidPrefix} /></div>}
    </>
  );
}
